import type { Mensaje } from './consultas'
import { formatearHora } from './formato'

interface Props {
  mensaje: Mensaje
}

/**
 * Una burbuja de la conversacion.
 *
 * El rol `herramienta` **no pinta su contenido**. Son 1.369 mensajes de los
 * 10.848 y su contenido es un array JSON con el resultado de una consulta
 * interna: el panel viejo los volcaba tal cual en la pantalla del operador.
 * Aqui se resumen en una linea que dice que se consulto y a que.
 *
 * El texto de las burbujas se pinta tal y como llega. El backend ya le ha
 * quitado la cabecera `Fecha/Usuario/Telefono` y el JSON de la respuesta; si un
 * mensaje trae un emoji es porque lo escribio el cliente, y se muestra.
 */
export function Burbuja({ mensaje }: Props) {
  if (mensaje.rol === 'herramienta') {
    // La frase entera va en un solo elemento: partir el nombre en un `span`
    // aparte para resaltarlo deja "Consultó" y "consultar_pedido" en dos nodos
    // distintos, y entonces ni un lector de pantalla ni una busqueda de texto
    // leen la linea como una frase. El nombre se distingue por el estilo de
    // toda la pastilla, que ya es distinto del de una burbuja.
    return (
      <li className="flex justify-center py-0.5">
        <span className="rounded-full bg-elevado px-3 py-1 text-xs text-tinta-apagada">
          Consultó {mensaje.herramienta ?? 'una herramienta'}
        </span>
      </li>
    )
  }

  const texto = mensaje.contenido.trim()
  // Una burbuja vacia no aporta nada: pasa cuando el asistente solo dejo
  // metadatos, que ya se ensenan en su panel.
  if (texto === '') return null

  const esUsuario = mensaje.rol === 'usuario'
  const hora = formatearHora(mensaje.fecha)

  return (
    <li className={`flex ${esUsuario ? 'justify-start' : 'justify-end'}`}>
      <div
        // La distincion cliente/asistente no depende de que el fondo sea
        // blanco: la burbuja del cliente es la superficie de tarjeta con su
        // anillo, y la del asistente un relleno de acento. En claro eso es
        // blanco sobre gris claro frente a azul; en oscuro, gris azulado con
        // borde frente al mismo azul. La forma (esquina achatada a un lado u
        // otro) y la alineacion siguen diciendo lo mismo en los dos temas.
        className={[
          'max-w-[85%] rounded-2xl px-3 py-2 sm:max-w-[75%]',
          esUsuario
            ? 'rounded-bl-sm bg-superficie text-tinta ring-1 ring-borde'
            : 'rounded-br-sm bg-burbuja text-burbuja-tinta',
        ].join(' ')}
      >
        <p className="whitespace-pre-wrap break-words text-sm">{texto}</p>
        {hora ? (
          <p
            className={`mt-1 text-[11px] ${
              esUsuario ? 'text-tinta-apagada' : 'text-burbuja-hora'
            }`}
          >
            {hora}
          </p>
        ) : null}
      </div>
    </li>
  )
}
